import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Form, Field } from 'redux-form';
import { List } from 'immutable';
import {
  RECORD_LEVEL_TYPE_COMPANY,
  RECORD_LEVEL_TYPE_CONTACT,
  RECORD_LEVEL_TYPE_ITEM,
} from '../../config/app_config';
import { storeWithExpiration } from '../../utils/common';
import FieldsetRow from './FieldsetRow';
import InputField from '../shared/InputField';

const FORM_ID = 'fieldsetForm';

export class FieldsetCreateEdit extends React.Component {
  static propTypes = {
    cancelAddEdit: PropTypes.func.isRequired,
    companyDatasetFields: PropTypes.object,
    contactDatasetFields: PropTypes.object,
    currDataset: PropTypes.object,
    currentFields: PropTypes.object, // If Edit, we should get some fields here
    deleteFieldsetFunction: PropTypes.func.isRequired,
    dirty: PropTypes.bool,
    fetchGroupFieldsByIdFunction: PropTypes.func.isRequired, // TODO: move this out of here!
    groupFields: PropTypes.object,
    groups: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    hasOrder: PropTypes.bool.isRequired,
    isEdit: PropTypes.bool.isRequired,
    isGroup: PropTypes.bool.isRequired,
    itemDatasetFields: PropTypes.object,
    progress: PropTypes.number.isRequired,
    submitFunction: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    companyDatasetFields: null,
    contactDatasetFields: null,
    currDataset: null,
    currentFields: null,
    dirty: false,
    groupFields: null,
    groups: null,
    itemDatasetFields: null,
  };

  constructor(props) {
    super(props);
    this.submitFieldset = this.submitFieldset.bind(this);
  }

  state = {
    checkValues: new List(),
    groups: null,
    isCheckedAllCompanies: false,
    isCheckedAllContacts: false,
    isCheckedAllItems: false,
    isDisplayingSelected: false,
    isEditFieldsAdded: false,
    recordLevelTypeGroups: [],
  };

  componentWillMount = () => {
    // Make sure everything is clear to start
    this.resetComponentState();
  };

  componentWillReceiveProps = () => {
    const { companyDatasetFields, contactDatasetFields, itemDatasetFields } = this.props;
    const { groups } = this.state;

    // Load display groups once the fields are available
    const hasContacts = contactDatasetFields && contactDatasetFields.size > 0;
    const hasCompanies = companyDatasetFields && companyDatasetFields.size > 0;
    const hasItems = itemDatasetFields && itemDatasetFields.size > 0;
    const hasFields = hasContacts || hasCompanies || hasItems;
    if (groups === null && hasFields) {
      // get groups into state to handle click toggling
      const recordLevelTypeGroups = [];
      if (hasContacts) {
        recordLevelTypeGroups.push({
          name: RECORD_LEVEL_TYPE_CONTACT,
          advancedParams: this.filterAdvanced(contactDatasetFields),
        });
      }
      if (hasCompanies) {
        recordLevelTypeGroups.push({
          name: RECORD_LEVEL_TYPE_COMPANY,
          advancedParams: this.filterAdvanced(companyDatasetFields),
        });
      }
      if (hasItems) {
        recordLevelTypeGroups.push({
          name: RECORD_LEVEL_TYPE_ITEM,
          advancedParams: this.filterAdvanced(itemDatasetFields),
        });
      }

      this.setState({ recordLevelTypeGroups });
      this.setGroupState(recordLevelTypeGroups);
    }
  };

  componentDidUpdate = () => {
    const { currentFields, isEdit, progress } = this.props;
    const { isEditFieldsAdded } = this.state;

    // If we're on an edit page currentFieldset will be populated
    // This will get called again on group checks, so ignore if editing has started
    // Only populate once all the new data is loaded (progress = 0)
    if (isEdit && progress === 0 && currentFields && currentFields.size > 0 && !isEditFieldsAdded) {
      // Format list
      const currentFieldsIdList = currentFields.map(currItem => currItem.get('fieldId').toString());

      // Set initial state
      // Set edit fields added
      this.setState(
        {
          checkValues: currentFieldsIdList,
          isEditFieldsAdded: true,
        },
        this.openGroupsWithChecks
      );
    }
  };

  setGroupState = (recordLevelTypeGroups, isEdit = false) => {
    const stateObj = {};
    recordLevelTypeGroups.forEach(type => {
      stateObj[type.name] = {};
      for (let i = 0; i < type.advancedParams.size; i += 1) {
        const { displayGroup } = type.advancedParams.get(i);
        if (displayGroup !== null && displayGroup !== undefined) {
          let isOpenGroup = false;
          if (isEdit) {
            const displayGroupFields = type.advancedParams.filter(
              field => field.get('displayGroup') === displayGroup
            );
            isOpenGroup = this.checkHasSelectedFields(displayGroupFields);
          }
          if (isOpenGroup) {
            stateObj[type.name][displayGroup.replace(/ /g, '')] = 'card-block';
          } else {
            stateObj[type.name][displayGroup.replace(/ /g, '')] = 'card-block hidden-xs-up';
          }
        } else {
          stateObj[type.name].None = 'card-block';
        }
      }
    });
    this.setState({
      groups: stateObj,
    });
  };

  setDisplayGroupAdvancedParams = trimmedParams => {
    const mappedGroups = trimmedParams.groupBy(param =>
      param.displayGroup !== null ? param.displayGroup : 'None'
    );
    return mappedGroups;
  };

  getClassName = (type, key, hasSelectedFields = false) =>
    this.state.groups &&
    this.state.groups[type] &&
    this.state.groups[type][key] &&
    !hasSelectedFields
      ? this.state.groups[type][key]
      : 'card-block';

  openGroupsWithChecks = () => {
    const { recordLevelTypeGroups } = this.state;
    this.setGroupState(recordLevelTypeGroups, true);
  };

  filterAdvanced = datasetFields => datasetFields.filter(field => !field.isSimpleSearch);

  filterSimple = datasetFields => datasetFields.filter(field => field.isSimpleSearch);

  groupAdvancedParams = trimmedParams => {
    const mappedGroups = trimmedParams.groupBy(param =>
      param.selectionGroup !== null ? param.selectionGroup : 'None'
    );
    return mappedGroups;
  };

  checkAll = (evt, fields, recordLevelType) => {
    const { checkValues } = this.state;
    const checkFields = evt.target.checked;
    const fieldsIdList = fields.map(currItem => currItem.get('id').toString());
    // retain a list of checked items in component state for submission
    let newCheckValues;
    if (checkFields) {
      if (recordLevelType === RECORD_LEVEL_TYPE_COMPANY) {
        this.setState({ isCheckedAllCompanies: true });
      } else if (recordLevelType === RECORD_LEVEL_TYPE_CONTACT) {
        this.setState({ isCheckedAllContacts: true });
      } else {
        this.setState({ isCheckedAllItems: true });
      }
    } else if (recordLevelType === RECORD_LEVEL_TYPE_COMPANY) {
      this.setState({ isCheckedAllCompanies: false });
    } else if (recordLevelType === RECORD_LEVEL_TYPE_CONTACT) {
      this.setState({ isCheckedAllContacts: false });
    } else {
      this.setState({ isCheckedAllItems: false });
    }

    if (checkFields) {
      newCheckValues = fieldsIdList
        .filter(field => !checkValues.includes(field))
        .concat(checkValues);
    } else {
      // Else remove group fields from SKU
      newCheckValues = checkValues.filter(field => !fieldsIdList.includes(field));
    }

    this.setState({ checkValues: newCheckValues });
  };

  checkAllCompany = evt =>
    this.checkAll(evt, this.props.companyDatasetFields, RECORD_LEVEL_TYPE_COMPANY);

  checkAllContact = evt =>
    this.checkAll(evt, this.props.contactDatasetFields, RECORD_LEVEL_TYPE_CONTACT);

  checkAllItem = evt => this.checkAll(evt, this.props.itemDatasetFields, RECORD_LEVEL_TYPE_ITEM);

  checkMulti = fields => evt => {
    this.checkAll(evt, fields, null);
  };

  updateCheck = evt => {
    const { checkValues } = this.state;
    const isChecked = evt.target.checked;
    const checkId = evt.target.id.toString();
    let updatedChecks;

    if (isChecked) updatedChecks = checkValues.push(checkId);
    else updatedChecks = checkValues.filter(val => val !== checkId);
    this.setState({ checkValues: updatedChecks });
  };

  handleJumpToContacts = () => {
    this.contactTable.scrollIntoView();
  };

  handleJumpToCompanies = () => {
    this.contentTop.scrollIntoView();
  };

  resetComponentState = () => {
    this.setState({ checkValues: new List() });
    this.setState({ isCheckedAllCompanies: false });
    this.setState({ isCheckedAllContacts: false });
    this.setState({ isCheckedAllItems: false });
    this.setState({ isDisplayingSelected: false });
    this.setState({ isEditFieldsAdded: false });
    this.setState({ recordLevelTypeGroups: [] });
  };

  clearList = () => {
    this.resetComponentState();
    this.setState({ isEditFieldsAdded: true });
  };

  toggleGroupDisplay = (type, trimmedKey) => evt => {
    const { state } = this;
    if (
      state.groups[type][trimmedKey] &&
      state.groups[type][trimmedKey] === 'card-block hidden-xs-up'
    ) {
      state.groups[type][trimmedKey] = 'card-block';
    } else {
      state.groups[type][trimmedKey] = 'card-block hidden-xs-up';
    }
    this.setState(state);
    evt && evt.preventDefault();
  };

  toggleDisplaySelectedFields = () => {
    const updatedIsDisplayingSelected = !this.state.isDisplayingSelected;
    this.setState({ isDisplayingSelected: updatedIsDisplayingSelected });
  };

  toggleGroupFieldsInTable = (groupFields, isAddGroup) => {
    const { checkValues } = this.state;
    // Format list
    const groupFieldsIdList = groupFields.map(currItem => currItem.get('fieldId').toString());
    let newCheckValues;

    // If adding group, add non-duplicate ids to the fieldset
    if (isAddGroup) {
      newCheckValues = groupFieldsIdList
        .filter(field => !checkValues.includes(field))
        .concat(checkValues);
    } else {
      // Else remove group fields from SKU
      newCheckValues = checkValues.filter(field => !groupFieldsIdList.includes(field));
    }
    this.setState({ checkValues: newCheckValues });
  };

  checkHasSelectedFields = fields => {
    const { checkValues } = this.state;
    return fields.filter(field => checkValues.indexOf(field.get('id').toString()) >= 0).size > 0;
  };

  addGroupFieldsToTable = groupFields => this.toggleGroupFieldsInTable(groupFields, true);

  removeGroupFieldsFromTable = groupFields => this.toggleGroupFieldsInTable(groupFields, false);

  // Save a new SKU or Group or save edits
  submitFieldset = params => {
    const { submitFunction } = this.props;
    const { checkValues } = this.state;
    submitFunction(params, checkValues);
  };

  renderRecordLevelType = (datasetFields, type) => {
    const {
      isCheckedAllCompanies,
      isCheckedAllContacts,
      isCheckedAllItems,
      isDisplayingSelected,
    } = this.state;
    const simpleFields = this.filterSimple(datasetFields);
    const advancedFields = this.filterAdvanced(datasetFields);
    const hasSimpleFields = simpleFields && simpleFields.size > 0;
    const hasAdvancedFields = advancedFields && advancedFields.size > 0;
    // const trimmedAdvancedFields = this.trimAdvancedParams(simpleFields, allAdvancedFields);
    const groupedAdvancedFields = this.setDisplayGroupAdvancedParams(advancedFields);
    let title = '';
    let isCheckAll = false;
    let checkAllFunction = null;
    let hasSelectedFields = true;
    let hasSelectedSimpleFields = true;
    let hasSelectedAdvancedFields = true;

    if (type === RECORD_LEVEL_TYPE_COMPANY) {
      title = 'Account Fields';
      isCheckAll = isCheckedAllCompanies;
      checkAllFunction = this.checkAllCompany;
    } else if (type === RECORD_LEVEL_TYPE_CONTACT) {
      title = 'Contact Fields';
      isCheckAll = isCheckedAllContacts;
      checkAllFunction = this.checkAllContact;
    } else if (type === RECORD_LEVEL_TYPE_ITEM) {
      title = 'Item Fields';
      isCheckAll = isCheckedAllItems;
      checkAllFunction = this.checkAllItem;
    }

    if (isDisplayingSelected) {
      hasSelectedFields = this.checkHasSelectedFields(datasetFields);
      hasSelectedSimpleFields = this.checkHasSelectedFields(simpleFields);
      hasSelectedAdvancedFields = this.checkHasSelectedFields(advancedFields);
    }

    return (
      <div>
        <div className="row">
          <div className="col-xs-10 col-lg-8">
            <h1>
              {title}{' '}
              <span>
                <input
                  id="checkall"
                  type="checkbox"
                  checked={isCheckAll}
                  onChange={checkAllFunction}
                />
                <label htmlFor="checkall"> Check All {title}</label>
              </span>
            </h1>
          </div>
        </div>
        {isDisplayingSelected && !hasSelectedFields ? (
          <div className="row">
            <div className="col-xs-12">
              <p>No Selected {title}</p>
            </div>
          </div>
        ) : (
          <div>
            {hasSimpleFields && hasSelectedSimpleFields ? (
              <div className="row">
                <div className="col-xs-12">
                  <h3>Simple Search</h3>
                </div>
                {this.renderTable(simpleFields, type)}
              </div>
            ) : null}
            {hasAdvancedFields && hasSelectedAdvancedFields ? (
              <div className="row">
                <div className="col-xs-12">
                  <div>
                    <h3>Advanced Search</h3>
                  </div>
                </div>
                <div className="col-xs-12">
                  <div className="inner-tab-content">
                    <div className="row">
                      {this.renderAdvancedDisplayGroups(groupedAdvancedFields, type)}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  renderAdvancedDisplayGroups = (advancedGroups, type) => {
    const { isDisplayingSelected } = this.state;
    let hasSelectedFields = true;
    const displayGroupsArray = [];
    let groupedAdvancedFields = null;
    // Render ungrouped params first
    advancedGroups.forEach((values, key) => {
      if (key === 'None') {
        const groupKey = `group-${key}`;
        if (isDisplayingSelected) {
          hasSelectedFields = this.checkHasSelectedFields(values);
        }
        const groupClassName = this.getClassName(
          type,
          key,
          isDisplayingSelected && hasSelectedFields
        );
        groupedAdvancedFields = this.groupAdvancedParams(values);
        displayGroupsArray.push(
          <div
            className={
              hasSelectedFields ? 'col-xs-12 param-group' : 'col-xs-12 param-group hide-section'
            }
            key={groupKey}
          >
            <div className="card">
              <div
                tabIndex={key}
                role="button"
                className="card-header"
                onClick={this.toggleGroupDisplay(type, key)}
                onKeyPress={() => null}
              >
                <h3>General</h3>
              </div>
              <div className={groupClassName}>
                <div className="row">
                  {this.renderAdvancedFieldGroups(groupedAdvancedFields, type)}
                </div>
              </div>
            </div>
          </div>
        );
      }
    });
    // Then render grouped params
    advancedGroups.forEach((values, key) => {
      if (key !== 'None') {
        const groupKey = `group-${key}`;
        const trimmedKey = key.replace(/ /g, '');
        if (isDisplayingSelected) {
          hasSelectedFields = this.checkHasSelectedFields(values);
        }
        const groupClassName = this.getClassName(
          type,
          trimmedKey,
          isDisplayingSelected && hasSelectedFields
        );
        groupedAdvancedFields = this.groupAdvancedParams(values);
        displayGroupsArray.push(
          <div
            className={
              hasSelectedFields ? 'col-xs-12 param-group' : 'col-xs-12 param-group hide-section'
            }
            key={groupKey}
          >
            <div className="card">
              <div
                tabIndex={key}
                role="button"
                className="card-header"
                onClick={this.toggleGroupDisplay(type, trimmedKey)}
                onKeyPress={() => null}
              >
                {key !== 'None' ? <h3>{key}</h3> : <h3>Other Parameters</h3>}
              </div>
              <div className={groupClassName}>
                <div className="row">
                  {this.renderAdvancedFieldGroups(groupedAdvancedFields, type)}
                </div>
              </div>
            </div>
          </div>
        );
      }
    });
    return displayGroupsArray;
  };

  renderAdvancedFieldGroups = (fieldGroups, type) => {
    const { isDisplayingSelected } = this.state;
    let hasSelectedFields = true;
    const groupsArray = [];
    // Render ungrouped table first
    fieldGroups.forEach((values, key) => {
      if (key === 'None') {
        const groupKey = `group-${key}`;
        groupsArray.push(
          <div className="col-xs-12" key={groupKey}>
            <div className="row">{this.renderTable(values, type)}</div>
          </div>
        );
      }
    });
    // Then render grouped table
    fieldGroups.forEach((values, key) => {
      if (key !== 'None') {
        const groupKey = `group-${key}`;
        const trimmedKey = key ? key.replace(/ /g, '') : null;
        const groupClassName = this.getClassName(
          type,
          trimmedKey,
          isDisplayingSelected && hasSelectedFields
        );
        if (isDisplayingSelected) {
          hasSelectedFields = this.checkHasSelectedFields(values);
        }
        groupsArray.push(
          <div
            className={hasSelectedFields ? 'col-xs-12' : 'col-xs-12 hide-section'}
            key={groupKey}
          >
            <fieldset className="fieldset-group">
              <div className="fieldset-header">
                {key !== 'None' ? <h3>{key}</h3> : <h3>Other Parameters</h3>}
              </div>
              <div className={groupClassName}>
                <div className="row">{this.renderTable(values, type)}</div>
              </div>
            </fieldset>
          </div>
        );
      }
    });
    return groupsArray;
  };

  renderTable = (datasetFields, type) => {
    const { isDisplayingSelected } = this.state;
    let renderedFields = null;
    let hasSelectedFields = true;

    if (type === RECORD_LEVEL_TYPE_COMPANY) {
      renderedFields = datasetFields.valueSeq().map(this.renderCompanyFields);
    } else if (type === RECORD_LEVEL_TYPE_CONTACT) {
      renderedFields = datasetFields.valueSeq().map(this.renderContactFields);
    } else if (type === RECORD_LEVEL_TYPE_ITEM) {
      renderedFields = datasetFields.valueSeq().map(this.renderItemFields);
    }
    if (isDisplayingSelected) {
      hasSelectedFields = this.checkHasSelectedFields(datasetFields);
    }

    return (
      <div className={hasSelectedFields ? 'col-xs-12' : 'col-xs-12 hide-section'}>
        <table
          className={
            isDisplayingSelected
              ? 'table table-sm table display-selected-only'
              : 'table table-sm table'
          }
        >
          <thead>
            <tr>
              <th>
                <input type="checkbox" onChange={this.checkMulti(datasetFields)} />
              </th>
              <th>Display Name</th>
              <th>Field Name</th>
              <th>Field Type</th>
              <th>Max Field Length</th>
            </tr>
          </thead>
          {datasetFields === undefined || datasetFields.size < 1 ? null : (
            <tbody>{renderedFields}</tbody>
          )}
        </table>
      </div>
    );
  };

  renderFields = (input, type) => {
    const { checkValues } = this.state;
    const fieldId = input.id.toString();
    const isChecked = checkValues.includes(fieldId);

    return (
      <FieldsetRow
        id={input.id}
        key={input.id}
        tableEntries={input}
        isChecked={isChecked}
        updateCheck={this.updateCheck}
        type={type}
      />
    );
  };

  renderCompanyFields = input => this.renderFields(input, RECORD_LEVEL_TYPE_COMPANY);

  renderContactFields = input => this.renderFields(input, RECORD_LEVEL_TYPE_CONTACT);

  renderItemFields = input => this.renderFields(input, RECORD_LEVEL_TYPE_ITEM);

  renderGroupSelect = group => {
    const { currDataset, fetchGroupFieldsByIdFunction } = this.props;
    let datasetId = null;
    if (currDataset) datasetId = currDataset.get('id');
    return (
      <tr key={group.id}>
        <td>
          <input
            type="checkbox"
            onChange={event => {
              const { checked } = event.target;
              const prom = new Promise(resolve => {
                fetchGroupFieldsByIdFunction(
                  datasetId,
                  group.id,
                  resolve,
                  storeWithExpiration.get('token')
                );
              });
              prom.then(() => {
                if (checked) {
                  this.addGroupFieldsToTable(this.props.groupFields);
                } else {
                  this.removeGroupFieldsFromTable(this.props.groupFields);
                }
              });
            }}
          />
          &nbsp;
          <span>{group.name}</span>
        </td>
      </tr>
    );
  };

  render() {
    const {
      cancelAddEdit,
      companyDatasetFields,
      contactDatasetFields,
      currDataset,
      deleteFieldsetFunction,
      dirty,
      groups,
      handleSubmit,
      hasOrder,
      isEdit,
      isGroup,
      itemDatasetFields,
      submitting,
    } = this.props;
    const { isDisplayingSelected } = this.state;
    const hasContacts = contactDatasetFields && contactDatasetFields.size > 0;
    const hasCompanies = companyDatasetFields && companyDatasetFields.size > 0;
    const hasItems = itemDatasetFields && itemDatasetFields.size > 0;
    let currDatasetName = '';
    if (currDataset) currDatasetName = currDataset.name;

    return (
      <div
        className="card-border"
        ref={node => {
          this.contentTop = node;
        }}
      >
        <div className="card-header">
          <div className="card-header-title">List: {currDatasetName}</div>
        </div>
        <div className="card-block">
          <div className="row">
            <div className="col-md-12">
              <Form id="fieldsetForm" onSubmit={handleSubmit(this.submitFieldset)}>
                <fieldset className="sku-group-name">
                  <Field
                    name="fieldsetName"
                    type="text"
                    component={InputField}
                    label={isGroup ? 'Group Name' : 'SKU Name'}
                    placeholder="Name"
                  />
                </fieldset>
                {isGroup ? null : (
                  <div>
                    <label htmlFor="table-add-group">Add Group</label>
                    <br />
                    <div className="pre-scrollable" style={{ maxHeight: 100 }}>
                      <table className="table" id="table-add-group">
                        <tbody>
                          {groups === undefined || groups.size === 0 ? (
                            <tr>
                              <td>
                                <p>There are no Groups associated with this dataset</p>
                              </td>
                            </tr>
                          ) : (
                            groups.map(group => this.renderGroupSelect(group))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <br />
                  </div>
                )}
                <div className="pull-right">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      cancelAddEdit(dirty);
                    }}
                  >
                    Cancel
                  </button>
                  {isEdit ? (
                    <button type="button" className="btn btn-secondary" onClick={this.clearList}>
                      Clear List
                    </button>
                  ) : null}
                  {isEdit && !hasOrder ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={deleteFieldsetFunction}
                    >
                      Delete {isGroup ? 'Group' : 'SKU'}
                    </button>
                  ) : null}
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    Save {isGroup ? 'Group' : 'SKU'}
                  </button>
                  <br />
                </div>
              </Form>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <hr />
              <div className="pull-right">
                {hasContacts && hasCompanies ? (
                  <button
                    type="button"
                    className="btn btn-secondary escape"
                    onClick={this.handleJumpToContacts}
                  >
                    Jump To Contact Fields
                  </button>
                ) : null}
                <button
                  type="button"
                  className={isDisplayingSelected ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={this.toggleDisplaySelectedFields}
                >
                  {isDisplayingSelected ? 'Show All Fields' : 'Show Only Selected Fields'}
                </button>
              </div>
              <br />
              {hasCompanies
                ? this.renderRecordLevelType(companyDatasetFields, RECORD_LEVEL_TYPE_COMPANY)
                : null}
              <div
                className="jump-reference"
                ref={node => {
                  this.contactTable = node;
                }}
              />
              {hasContacts && hasCompanies ? <br /> : null}
              {hasContacts && hasCompanies ? (
                <div className="pull-right">
                  <button
                    type="button"
                    className="btn btn-secondary escape"
                    onClick={this.handleJumpToCompanies}
                    style={{ position: 'absolute', right: '0.975rem', marginTop: '-1rem' }}
                  >
                    Back To Top
                  </button>
                </div>
              ) : null}
              {hasContacts
                ? this.renderRecordLevelType(contactDatasetFields, RECORD_LEVEL_TYPE_CONTACT)
                : null}
              {hasItems
                ? this.renderRecordLevelType(itemDatasetFields, RECORD_LEVEL_TYPE_ITEM)
                : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const validate = values => {
  const errors = {};
  if (values.fieldsetName.length > 80) {
    errors.fieldsetName = 'Must be less than 80 characters';
  }
  if (!values.fieldsetName || values.fieldsetName.trim() === '') {
    errors.fieldsetName = 'Required';
  }
  return errors;
};

export default reduxForm({
  form: FORM_ID,
  validate,
  enableReinitialize: true,
})(FieldsetCreateEdit);
